import type { FC } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import AutocompleteRHF from '$components/Autocomplete/AutocompleteRHF';
import classes from './AddNoteForm.module.css';
import BaseTextArea from '../../../../TextArea/BaseTextArea';
import SaveButton from '../../../../Buttons/SaveButton';
import { SaveState } from '../../../../../types/ForrmType';
import {
  useDoNotHireCandidateMutation,
  useSaveCandidateNoteMutation,
} from '../../../../../store/modules/candidates';

interface FormData {
  Title: string;
  Content: string;
}

interface Props {
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  positionId?: number;
  isNotHirable: boolean;
}

const AddNoteForm: FC<Props> = ({ setIsOpen, positionId, isNotHirable }) => {
  const {
    register,
    formState: { errors },
    watch,
    handleSubmit,
    setValue,
  } = useForm<FormData>({
    defaultValues: {
      Title: '',
      Content: '',
    },
    mode: 'onChange',
  });
  const [t] = useTranslation('global');
  const title = register('Title', {
    required: `${t('Notes.TitleError')}`,
    maxLength: {
      value: 100,
      message: `${t('Notes.TitleErrorLength')}`,
    },
  });
  const content = register('Content', {
    required: `${t('Notes.ContentError')}`,
  });
  const contentValue = watch('Content');
  const { candidateId } = useParams();
  const [saveCandidateNote] = useSaveCandidateNoteMutation();
  const [doNotHireCandidate] = useDoNotHireCandidateMutation();

  const saveNote = async (data: FormData) => {
    const CandidateId = Number(candidateId!);
    const PositionId =
      Number(positionId!) === 0 ? undefined : Number(positionId!);
    const noteTitle = data.Title.trim().toLowerCase();
    if (noteTitle === 'do not hire') {
      await doNotHireCandidate({ candidateId, doNotHire: true });
    } else if (noteTitle === 'make available') {
      await doNotHireCandidate({ candidateId, doNotHire: false });
    }

    await saveCandidateNote({
      data: { ...data, CandidateId, PositionId },
      lang: 'en',
    });
    setIsOpen(false);
  };

  const hasErrors = Object.keys(errors).length > 0;

  const hirableOptions = !isNotHirable
    ? { Id: 0, title: 'Do not hire' }
    : { Id: 1, title: 'Make Available' };

  return (
    <form onSubmit={handleSubmit(saveNote)} data-testid="add-note-form">
      <div className={classes.content}>
        <AutocompleteRHF
          label={t('Notes.Title')}
          name={title.name}
          onBlur={title.onBlur}
          onChange={title.onChange}
          refProp={title.ref}
          options={[hirableOptions]}
          setValue={setValue}
          placeholder="Title"
          enableNewValues
        />
        <BaseTextArea
          name={content.name}
          onChange={content.onChange}
          label={t('Notes.Content')}
          onBlur={content.onBlur}
          refProp={content.ref}
          counter={contentValue.length}
          maxLength={4096}
          errorText={errors.Content?.message}
          secondary
        />
        <div className={classes.actions}>
          <button
            type="button"
            className={classes.normalButton}
            onClick={() => setIsOpen(false)}
          >
            {t('ConfirmAddToPositionModal.goBack')}
          </button>
          <SaveButton
            submit
            t={t}
            state={SaveState.IDLE}
            disabled={hasErrors}
          />
        </div>
      </div>
    </form>
  );
};

AddNoteForm.defaultProps = {
  positionId: 0,
};

export default AddNoteForm;
